<template>
  <div
    id="mobile-filter"
    data-controller="mobile-filter-position"
    class="mt-3 sticky z-10 bg-white text-sm top-0 transition-all duration-500 before:content-[''] before:absolute before:-top-4 before:left-0 before:w-full before:h-4 before:bg-white"
  >
    <nav class="flex items-center space-x-5 overflow-x-auto _scrollbar--hidden">
      <button
        @click="openFiltersList"
        class="items-center px-5 py-3 text-blackest rounded-lg focus:outline-hidden border-[1.5px] relative"
      >
        <FaIcon :icon="['fas', 'sliders']" />
        <span
          v-if="filtersCount > 0 || sortCount.badgeCondition"
          class="flex items-center justify-center size-6 text-xs text-white bg-primary rounded-full absolute top-1 right-2"
        >
          {{ filtersCount + sortCount.badgeContent }}
        </span>
      </button>

      <button
        v-for="{ value, label, badgeContent, badgeCondition } in options"
        :key="value"
        @click="openFilter(value)"
        class="shrink-0 flex items-center space-x-3 px-5 py-3 text-blackest rounded-lg focus:outline-hidden border-[1.5px]"
      >
        {{ label }}
        <span v-if="!badgeCondition">
          <FaIcon :icon="['fas', 'chevron-down']" class="ml-3" />
        </span>
        <span
          v-if="badgeCondition"
          class="flex items-center justify-center size-7 text-xs text-white bg-primary rounded-full ml-3"
          >{{ badgeContent }}
        </span>
      </button>
    </nav>

    <div class="z-30" @click.self="close()" v-if="refine || sort">
      <div class="w-full h-full fixed top-0 left-0 bg-blackest opacity-35"></div>
      <MobileFilterFull v-if="refine">
        <template v-slot:header>
          <MobileHeader
            :show-back-btn="showBackBtn && (refining || sort)"
            :handle-back="back"
            :label="refineLabel"
            :handle-close="close"
          />
        </template>

        <template v-slot:body>
          <div class="MobileFilterFull_body flex flex-col space-y-6 px-10 pt-0" ref="MobileFilterFullBody">
            <template v-if="!refining">
              <Refine :select="setRefining" :showPrizeCategories="prizeCategories.length > 0" />
            </template>

            <template v-if="refining === 'prices'">
              <div>
                <PricesMultiRangeSlider
                  :min="0"
                  :gap="100"
                  :max="6000"
                  :step="100"
                  ref="pricesMultiRangeSlider"
                  @select="selectPriceRange"
                />

                <MultiSelectFilter filter-type="prices" :options="listingFilters.prices" />

                <div class="border-b mb-6" />

                <div class="flex items-center mb-6" v-if="currentMarket.isCore">
                  <button
                    type="button"
                    :class="framing_filter_preference ? 'bg-bt-blue-500' : 'bg-gray-200'"
                    class="relative inline-flex h-10 w-17 shrink-0 items-center cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-hidden"
                    role="switch"
                    @click="toggleFramingPreference"
                  >
                    <span
                      aria-hidden="true"
                      :class="framing_filter_preference ? 'translate-x-8' : 'translate-x-1'"
                      class="pointer-events-none inline-block h-8 w-8 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    ></span>
                  </button>
                  <span class="ml-6 font-normal">Show Price Including Frame</span>
                </div>
              </div>
            </template>

            <MultiSelectFilter v-if="refining === 'art_styles'" filter-type="art_styles" :options="art_styles" />
            <CategoriesSelectFilter v-if="refining === 'categories'" />
            <MultiSelectFilter v-if="refining === 'collections'" filter-type="collections" :options="collections" />
            <MultiSelectFilter v-if="refining === 'mediums'" filter-type="mediums" :options="mediums" />
            <SizeFilter v-if="refining === 'sizes'" />
            <MultiSelectFilter
              v-if="refining === 'prize_categories' && prizeCategories.length"
              filter-type="competition_category_ids"
              :options="prizeCategories"
            />
            <OrientationFilter v-if="refining === 'orientations'" />
            <MultiSelectFilter
              v-if="refining === 'artwork_status'"
              filter-type="artwork_statuses"
              :options="artworkStatuses"
            />
            <ColorFilter :options="colors" v-if="refining === 'colors'" />
            <MultiSelectFilter v-if="refining === 'locations'" filter-type="locations" :options="locations" />
            <CheckboxFilterOptions v-if="refining === 'displays'" />
          </div>
        </template>

        <template v-slot:footer>
          <MobileFooter :handle-clear="clear" :handle-close="close" />
        </template>
      </MobileFilterFull>

      <MobileFilterFull class="sort" label="Sort By" @close="close" v-if="sort">
        <template v-slot:header>
          <MobileHeader
            :show-back-btn="showBackBtn && (refining || sort)"
            :handle-back="back"
            label="Sort"
            :handle-close="close"
          />
        </template>

        <template v-slot:body>
          <div class="MobileFilterFull_body flex flex-col px-10 pt-0" ref="MobileFilterFullBody">
            <div class="space-y-6">
              <PerPage />
              <SortFilter v-if="!gp1kActive" :options="sortOptions" />
              <ViewType />
            </div>
          </div>
        </template>

        <template v-slot:footer>
          <MobileFooter :handle-clear="clear" :handle-close="close" />
        </template>
      </MobileFilterFull>
    </div>
  </div>
</template>

<script setup>
import useCategoriesCount from "@/pages/ListingsPage/filters/mobile/hooks/useCategoriesCount"
import useCommonCount from "@/pages/ListingsPage/filters/mobile/hooks/useCommonCount"
import useDisplayCount from "@/pages/ListingsPage/filters/mobile/hooks/useDisplayCount"
import useSizeCount from "@/pages/ListingsPage/filters/mobile/hooks/useSizeCount"
import useSortCount from "@/pages/ListingsPage/filters/mobile/hooks/useSortCount"
import MobileFooter from "@/pages/ListingsPage/filters/mobile/MobileFooter.vue"
import MobileHeader from "@/pages/ListingsPage/filters/mobile/MobileHeader.vue"
import { computed } from "vue"
import usePriceCount from "@/pages/ListingsPage/filters/mobile/hooks/usePriceCount"
import PricesMultiRangeSlider from "@/pages/ListingsPage/filters/PricesMultiRangeSlider.vue"
import MobileFilterFull from "./MobileFilterFull.vue"
import Refine from "./Refine.vue"
import ViewType from "./ViewType.vue"
import PerPage from "./PerPage.vue"
import MultiSelectFilter from "../MultiSelectFilter.vue"
import CategoriesSelectFilter from "../CategoriesSelectFilter.vue"
import ColorFilter from "../ColorFilter.vue"
import SortFilter from "../SortFilter.vue"
import SizeFilter from "./SizeFilter.vue"
import OrientationFilter from "./OrientationFilter.vue"
import CheckboxFilterOptions from "../CheckboxFilterOptions.vue"
import { mapState } from "vuex"
const priceCount = usePriceCount()
const sizeCount = useSizeCount()
const sortCount = useSortCount()
const categoriesCount = useCategoriesCount()
const displayCount = useDisplayCount()
const subjectCount = useCommonCount("collections")
const colorsCount = useCommonCount("colors")
const styleCount = useCommonCount("art_styles")
const orientationCount = useCommonCount("orientations")
const mediumCount = useCommonCount("mediums")
const locationCount = useCommonCount("locations")
const options = computed(() =>
  [
    { label: "Price", value: "prices", ...priceCount.value },
    { label: "Size", value: "sizes", ...sizeCount.value },
    {
      label: "Medium",
      value: "mediums",
      ...mediumCount.value,
    },
    {
      label: "Colour",
      value: "colors",
      ...colorsCount.value,
    },
    {
      label: "Categories",
      value: "categories",
      ...categoriesCount.value,
    },
    {
      label: "Subject",
      value: "collections",
      ...subjectCount.value,
    },
    {
      label: "Style",
      value: "art_styles",
      ...styleCount.value,
    },
    {
      label: "Orientation",
      value: "orientations",
      ...orientationCount.value,
    },
    {
      label: "Artist location",
      value: "locations",
      ...locationCount.value,
    },
    {
      label: "Display",
      value: "displays",
      ...displayCount.value,
    },
  ].filter((option) => !option.hidden),
)
</script>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import ListingFilters, { gp1kExperimentRunning } from "@/lib/ListingFilters"
import { i18n } from "@/lib/vue/plugins/i18n"
import eventBus from "@/lib/vue/plugins/event_bus"

export default {
  name: "MobileFilter",
  props: {
    prizeCategories: Array,
  },
  data() {
    return {
      refine: false,
      sort: false,
      refining: null,
      filterButtonStyles:
        "ml-2 col-span-3 flex items-center justify-center gap-3 border border-black font-bold text-lg rounded-full",
      currency_sign: i18n.tc("market.currency.sign"),
      showBackBtn: false,
      gp1kActive: false,
    }
  },
  mounted() {
    this._unsubscribeGp1k = window.posthog?.onFeatureFlags?.(() => {
      this.gp1kActive = gp1kExperimentRunning()
    })
  },
  beforeDestroy() {
    this._unsubscribeGp1k?.()
  },
  methods: {
    popup(type) {
      switch (type) {
        case "refine":
          this.refine = true
          this.sort = false
          break
        case "sort":
          this.refine = false
          this.sort = true
          break
      }
      document.getElementById("main-navbar").style.zIndex = 5
      document.body.style.overscrollBehavior = "contain"
      document.body.classList.add("listing-filtering")
      document.body.classList.add("hide-intercom")
      eventBus.$emit("mobile-filter:open")
    },
    openFiltersList() {
      this.popup("refine")
      this.showBackBtn = true
    },
    back() {
      this.popup("refine")
      this.sort = false
      this.refining = null
    },
    close() {
      this.refine = false
      this.sort = false
      this.refining = null
      this.showBackBtn = false
      document.body.classList.remove("listing-filtering")
      document.body.classList.remove("hide-intercom")
      document.getElementById("main-navbar").style.zIndex = 20
      document.body.style.overscrollBehavior = "auto"
    },
    setRefining(type) {
      if (type === "sort") return this.popup("sort")
      this.refining = type
      this.resetBodyScroll()
    },
    openFilter(value) {
      this.popup("refine")
      this.setRefining(value)
    },
    clear() {
      if (this.refining) {
        this.$refs[this.refining]?.reset?.()
        let filters = []

        if (this.refining === "categories") filters = ["mediums"]
        if (this.refining === "displays") filters = ["availability", "ready_to_hang", "from_followeds"]
        if (filters.length === 0) filters = [this.refining]

        this.clearFilter(filters)
      } else if (this.sort) {
        this.clearFilter(["sort", "page", "per"])
      } else {
        Object.values(this.$refs).forEach((v) => v?.reset?.())
        this.resetFilters()
      }
      this.close()
    },
    resetBodyScroll() {
      if (this.$refs.MobileFilterFullBody?.scrollTop) {
        this.$refs.MobileFilterFullBody.scrollTop = 0
      }
    },
    selectPriceRange({ price_range }) {
      this.mergeFilter({ price_range })
    },
    toggleFramingPreference() {
      this.mergeFilter({ framing_filter_preference: !this.framing_filter_preference })
    },
    ...mapActions("filter", ["clearFilter", "resetFilters", "mergeFilter", "mergeDimensions"]),
  },
  computed: {
    refineLabel: function () {
      return !this.refining ? "" : ListingFilters.refiningLabelMap[this.refining]
    },
    locations: function () {
      return ListingFilters.locations
    },
    sortOptions: function () {
      return ListingFilters.sortKeys()
    },
    listingFilters() {
      return ListingFilters
    },
    artworkStatuses: function () {
      return ListingFilters.artwork_statuses
    },

    ...mapState("filter", {
      prices: ({ filters }) => filters.prices,
      colors: (state) => state.config.colors,
      mediums: (state) => state.config.mediums,
      collections: (state) => state.config.collections,
      art_styles: (state) => state.config.art_styles,
      artwork_statuses: (state) => state.config.artwork_statuses,
      price_range: ({ filters }) => filters.price_range,
      framing_filter_preference: ({ filters }) => filters.framing_filter_preference,
    }),
    ...mapGetters("filter", ["filtersCount"]),
  },
}
</script>
